import type { TypeSafeTableColumn } from "@dealer-center/core-components";
import {
  Alert,
  Button,
  Card,
  CardFooter,
  PencilSquareIcon,
  SearchInput,
  Table,
  TrashIcon
} from "@dealer-center/core-components";
import { LoadingIndicator } from "@dealer-center/core-custom-components";

import { useEffect, useState } from "react";
import type { RouteObject } from "react-router-dom";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import {
  useDealerCodesGet,
  useDealerCodesGetTypes
} from "@dealer-center/ddc-pages-dealer-codes-fetch";
import type { dealerCodeData } from "@dealer-center/ddc-pages-dealer-codes-interface";
import { AccountLink } from "@dealer-center/ddc-core-custom-components";
import { useDealerCodeModals } from "../modals";

const StyledPagesDealerCodes = styled.div``;

const StyledTable = styled(Table.Table)`
  width: 100%;
`;
export const route = {
  path: "dealer-codes/:dealercode?",
  element: <PagesDealerCodes />
} as RouteObject;

export function PagesDealerCodes() {
  const { dealercode: dealerCodeParam } = useParams();
  const [
    selectedDealerCodeTypeFromDropdown,
    setSelectedDealerCodeTypeFromDropdown
  ] = useState<string | undefined>(dealerCodeParam);
  const dealerCodeModals = useDealerCodeModals();
  const {
    data: dealerCodeList,
    isLoading: dealerCodeListLoading,
    error: dealerCodeListError
  } = useDealerCodesGet(selectedDealerCodeTypeFromDropdown);
  const {
    data: dealercodesTypeList,
    isLoading: dealercodesTypeListLoading,
    error: dealercodesTypeListError
  } = useDealerCodesGetTypes();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Dealer Codes";
  }, []);

  const resultsToCsvStringWithHeader = (results: dealerCodeData[]) => {
    const header = Object.keys(results[0]).join(",");
    const rows = results.map(result => {
      return Object.values(result).join(",");
    });
    return [header, ...rows].join("\n");
  };

  const downloadResultsClicked = async () => {
    if (!dealerCodeList) return;
    const csvString = resultsToCsvStringWithHeader(dealerCodeList);
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = selectedDealerCodeTypeFromDropdown + ".csv";
    link.href = url;
    link.click();
  };
  if (dealercodesTypeListError) {
    return (
      <Alert role="alert" type="error">
        <p>There was an error loading the dealer code types.</p>
      </Alert>
    );
  }
  if (dealercodesTypeListLoading || !dealercodesTypeList) {
    return <LoadingIndicator actionName="Dealer Codes Type Loading" />;
  }

  const dealerCodeTableColumns: TypeSafeTableColumn<dealerCodeData>[] = [
    {
      title: "Account",
      dataIndex: "accountId",
      render: (text: string) => (
        <AccountLink
          accountID={text}
          component={"client-search"}
          sameWindow={false}
        />
      )
    },
    {
      title: "Dealer Code",
      dataIndex: "dealerCode"
    },
    {
      title: "Manage Dealer Codes",
      dataIndex: "accountId",
      className: "column-extraData",
      columnSortable: false,
      render: (accountId: string) => (
        <Button
          analyticsConfiguration={{
            eventName: "Dealer Code Manage"
          }}
          buttonStyle="aux"
          data-testid={`manage-dealer-codes-${accountId}`}
          onClick={() => {
            window.open(
              `${window.location.origin}/landing/dealer/${accountId}/dashboard/dealerCodes`,
              "_blank"
            );
          }}
        >
          Manage
        </Button>
      )
    },
    {
      title: "Edit",
      dataIndex: "accountId",
      columnSortable: false,
      render: (accountId: string, dealerCode: dealerCodeData) => (
        <Button
          analyticsConfiguration={{
            eventName: "Dealer Code Edit"
          }}
          buttonStyle="aux"
          data-testid={`edit-dealer-code-${dealerCode.dealerCode}-${accountId}`}
          onClick={() => {
            dealerCodeModals.showEditModal({
              account: {
                id: accountId
              },
              dealerCode: dealerCode.dealerCode,
              dealerCodeType: selectedDealerCodeTypeFromDropdown || "",
              id: dealerCode.id
            });
          }}
        >
          <PencilSquareIcon title="Apply" />
        </Button>
      )
    },
    {
      title: "Delete",
      dataIndex: "accountId",
      columnSortable: false,
      render: (accountId: string, dealerCode: dealerCodeData) => (
        <Button
          analyticsConfiguration={{
            eventName: "Dealer Code Delete"
          }}
          buttonStyle="aux"
          data-testid={`delete-dealer-code-${dealerCode.dealerCode}-${accountId}`}
          onClick={() => {
            dealerCodeModals.showDeleteModal({
              account: {
                id: accountId
              },
              dealerCodeType: selectedDealerCodeTypeFromDropdown || "",
              id: dealerCode.id
            });
          }}
        >
          <TrashIcon title="Delete" />
        </Button>
      )
    }
  ];
  return (
    <StyledPagesDealerCodes>
      <h1>Dealer Codes</h1>
      <div>
        <SearchInput
          inputValue={selectedDealerCodeTypeFromDropdown}
          name={"dealerCodeTypeSelectInput"}
          options={dealercodesTypeList.map(dealerCodeType => {
            return {
              label: dealerCodeType,
              id: dealerCodeType
            };
          })}
          placeholder="Dealer Code Type"
          onChange={event => {
            const selectedItem = event.target.value;
            const selectedValue =
              selectedItem && selectedItem.id ? selectedItem.id : "";
            navigate(`/dealer-codes/${selectedValue}`);
            setSelectedDealerCodeTypeFromDropdown(selectedValue);
          }}
          onClearCallback={() => {
            navigate("/dealer-codes");
            setSelectedDealerCodeTypeFromDropdown(undefined);
          }}
        />
      </div>
      {dealerCodeListError && (
        <Alert role="alert" type="error">
          <p>There was an error loading the dealer codes.</p>
        </Alert>
      )}
      {dealerCodeListLoading && (
        <LoadingIndicator actionName="Dealer Codes Loading" />
      )}
      {selectedDealerCodeTypeFromDropdown && dealerCodeList && (
        <Card
          footer={
            <CardFooter
              actions={[
                {
                  label: "Add Code to Dealer",
                  onClick: () => {
                    dealerCodeModals.showAddCodeToDealerModal({
                      type: selectedDealerCodeTypeFromDropdown,
                      code: "",
                      id: ""
                    });
                  }
                },
                {
                  label: "Download Results",
                  onClick: () => {
                    downloadResultsClicked();
                  }
                }
              ]}
            />
          }
          header={`Dealer Code ${selectedDealerCodeTypeFromDropdown} (${dealerCodeList.length})`}
          sx={{ marginTop: "16px", padding: "8px" }}
        >
          <StyledTable
            displayFilter
            displayPageSizeSelector
            sortableColumns
            columns={dealerCodeTableColumns}
            data={dealerCodeList}
            defaultPageSize={20}
            emptyText="No dealer codes found"
            scrollX={500}
            tableLayout="auto"
          />
        </Card>
      )}
      {dealerCodeModals.modal}
    </StyledPagesDealerCodes>
  );
}

export default PagesDealerCodes;
